import React, { useState } from "react";
import { motion } from "framer-motion";
import { exportPurchaseData, generateSpendingReportHTML } from "../utils/exportUtils";

const ExportData = ({ purchases, theme, onClose }) => {
  const [exportFormat, setExportFormat] = useState("csv");
  const [reportPeriod, setReportPeriod] = useState("month");
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };
  
  // Handle export
  const handleExport = () => {
    exportPurchaseData(purchases, exportFormat);
  };
  
  // Handle report generation
  const handleGenerateReport = () => {
    const reportHTML = generateSpendingReportHTML(purchases, reportPeriod);
    
    // Create a new window with the report
    const reportWindow = window.open('', '_blank');
    reportWindow.document.write(reportHTML);
    reportWindow.document.close();
  };
  
  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 w-full max-w-md"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold dark:text-white">Export Your Data</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
          >
            ✕
          </button>
        </div>
        
        <div className="space-y-6">
          {/* Data Export Section */}
          <div>
            <h3 className="font-semibold mb-3 dark:text-white">Export Purchase History</h3>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-4">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Export all your purchase data for use in spreadsheets or other apps.
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Export Format
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setExportFormat("csv")}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                      exportFormat === "csv" 
                        ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400" 
                        : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300"
                    }`}
                  >
                    CSV (Excel)
                  </button>
                  <button
                    onClick={() => setExportFormat("json")}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                      exportFormat === "json" 
                        ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400" 
                        : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300"
                    }`}
                  >
                    JSON (Developer)
                  </button>
                </div>
              </div>
              
              <motion.button
                onClick={handleExport}
                className={`w-full py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Export Purchase History
              </motion.button>
            </div>
          </div>
          
          {/* Report Generation Section */}
          <div>
            <h3 className="font-semibold mb-3 dark:text-white">Generate Spending Report</h3>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-4">
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Create a printable spending report to review your shopping patterns.
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Report Period
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setReportPeriod("month")}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                      reportPeriod === "month" 
                        ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400" 
                        : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300"
                    }`}
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => setReportPeriod("year")}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                      reportPeriod === "year" 
                        ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400" 
                        : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300"
                    }`}
                  >
                    This Year
                  </button>
                  <button
                    onClick={() => setReportPeriod("all")}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                      reportPeriod === "all" 
                        ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400" 
                        : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300"
                    }`}
                  >
                    All Time
                  </button>
                </div>
              </div>
              
              <motion.button
                onClick={handleGenerateReport}
                className={`w-full py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Generate Report
              </motion.button>
            </div>
          </div>
          
          {/* Privacy Notice */}
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-4 text-xs text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">Privacy Notice</p>
            <p>
              Your purchase data is stored locally on your device only. Exporting creates a file
              on your device that you can choose to share or keep private.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ExportData;
